import { Suspense, lazy, useCallback, useMemo, useState, type ReactNode } from "react";
import type { AddressLookupResult, OrgProfile, PostalAddress } from "@backstory/protocol";
import { Dialog, Field } from "../../components/ui";
import { countryName } from "../../lib/country";
import { isPhoneNumber } from "../../lib/phone";
import { formatOffset, timezoneGroups } from "../../lib/timezones";
import { US_STATES, isUnitedStates, usStateCode } from "../../lib/usStates";

// The flag artwork for every country is only wanted on these two screens, so
// it loads when they do rather than with the rest of the app.
const countryInputs = import("../../components/CountryInputs");
const PhoneInput = lazy(() => countryInputs.then((m) => ({ default: m.PhoneInput })));
const CountrySelect = lazy(() => countryInputs.then((m) => ({ default: m.CountrySelect })));

/** The browser's zone, which is what a new organization starts with. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Drops empty strings so two profiles compare by what is filled in. */
export function normalizeProfile(p: OrgProfile | undefined): OrgProfile {
  const out: OrgProfile = {};
  for (const [k, v] of Object.entries(p ?? {})) {
    const t = typeof v === "string" ? v.trim() : "";
    if (t) (out as Record<string, string>)[k] = t;
  }
  // The state dropdown stores the abbreviation, whichever way it was typed.
  if (isUnitedStates(out.country)) {
    const code = usStateCode(out.region);
    if (code) out.region = code;
  }
  return out;
}

const REQUIRED: Array<[keyof OrgProfile, string]> = [
  ["contactEmail", "Enter an email address."],
  ["phone", "Enter a phone number."],
  ["addressLine1", "Enter a street address."],
  ["city", "Enter a city."],
  ["region", "Enter a state or region."],
  ["postalCode", "Enter a postal code."],
  ["country", "Enter a country."],
];

/** Required contact and address, plus a contact email that is actually an address. Website and suite are optional. */
export function profileProblems(p: OrgProfile): Partial<Record<keyof OrgProfile, string>> {
  const out: Partial<Record<keyof OrgProfile, string>> = {};
  for (const [key, message] of REQUIRED) {
    if (!p[key]?.trim()) out[key] = message;
  }
  const email = p.contactEmail?.trim() ?? "";
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) out.contactEmail = "Enter an email address.";
  if (p.phone?.trim() && !isPhoneNumber(p.phone)) out.phone = "Enter a complete phone number.";
  // The United States picks from a fixed list, so a typed region is not a state.
  if (isUnitedStates(p.country)) {
    if (!usStateCode(p.region)) out.region = "Choose a state.";
    if (!p.postalCode?.trim()) out.postalCode = "Enter a ZIP code.";
  }
  return out;
}

const POSTAL_KEYS = ["addressLine1", "addressLine2", "city", "region", "postalCode", "country"] as const;

/** Just the postal part of a profile. */
export function postalOf(p: OrgProfile | undefined): PostalAddress {
  const out: PostalAddress = {};
  for (const k of POSTAL_KEYS) {
    const v = p?.[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}

/** Loose match: case, spacing, and punctuation do not make two addresses different. */
function loose(s: string | undefined): string {
  return (s ?? "")
    .toLocaleLowerCase()
    .replace(/[.,#-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** "California" and "CA" are the same state; everything else compares loosely. */
function sameRegion(a: PostalAddress, b: PostalAddress): boolean {
  if (isUnitedStates(a.country) || isUnitedStates(b.country)) {
    const ac = usStateCode(a.region);
    const bc = usStateCode(b.region);
    if (ac && bc) return ac === bc;
  }
  return loose(a.region) === loose(b.region);
}

export function sameAddress(a: PostalAddress, b: PostalAddress): boolean {
  return POSTAL_KEYS.every((k) => (k === "addressLine2" ? true : k === "region" ? sameRegion(a, b) : loose(a[k]) === loose(b[k])));
}

/** An address as it would be written on an envelope, one part per line. */
export function addressLines(a: PostalAddress): string[] {
  const region = isUnitedStates(a.country) ? usStateCode(a.region) || a.region : a.region;
  const cityLine = [a.city, [region, a.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [a.addressLine1, a.addressLine2, cityLine, countryName(a.country)].filter((s): s is string => !!s && s.trim() !== "");
}

/**
 * Checks a typed address against the lookup before it is saved. `confirm`
 * resolves with the profile to save: what was typed when the lookup agrees
 * (or has nothing), or whichever of the two the person picks in the dialog.
 * It resolves null when the dialog is dismissed, so the save does not happen.
 */
export function useAddressCheck(lookup: (a: PostalAddress) => Promise<AddressLookupResult>) {
  const [checking, setChecking] = useState(false);
  const [pending, setPending] = useState<{ entered: OrgProfile; suggested: PostalAddress; resolve: (p: OrgProfile | null) => void } | null>(null);

  const confirm = useCallback(
    async (profile: OrgProfile): Promise<OrgProfile | null> => {
      const entered = postalOf(profile);
      let res: AddressLookupResult | null = null;
      setChecking(true);
      try {
        res = await lookup(entered);
      } catch {
        // The lookup is a courtesy. When it is down the typed address stands.
        res = null;
      } finally {
        setChecking(false);
      }
      const suggested = res?.found ? res.suggestion : undefined;
      // A match comes back with the state's full name ("California"); the form
      // stores the abbreviation, so the two read as the same state.
      if (suggested && isUnitedStates(suggested.country ?? entered.country)) {
        const code = usStateCode(suggested.region);
        if (code) suggested.region = code;
      }
      if (!suggested || sameAddress(entered, suggested)) return profile;
      return new Promise((resolve) => setPending({ entered: profile, suggested, resolve }));
    },
    [lookup],
  );

  const settle = (p: OrgProfile | null) => {
    pending?.resolve(p);
    setPending(null);
  };

  const dialog: ReactNode = pending ? (
    <Dialog
      open
      title="Confirm address"
      subtitle="The address was matched to a postal record that reads a little differently."
      width={560}
      className="address-dialog"
      onClose={() => settle(null)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => settle(pending.entered)}>
            Keep what I entered
          </button>
          <button type="button" className="btn primary" onClick={() => settle({ ...pending.entered, ...pending.suggested, addressLine2: pending.entered.addressLine2 })}>
            Use suggested address
          </button>
        </>
      }
    >
      <div className="address-compare">
        <div>
          <h3>You entered</h3>
          <address>{addressLines(postalOf(pending.entered)).join("\n")}</address>
        </div>
        <div>
          <h3>Suggested</h3>
          <address>{addressLines(pending.suggested).join("\n")}</address>
        </div>
      </div>
    </Dialog>
  ) : null;

  return { confirm, checking, dialog };
}

/**
 * Contact details, postal address, and time zone for an organization. Used by
 * the General page and by first-run setup, so the two ask the same questions.
 */
export function OrgProfileFields({
  value,
  onChange,
  errors = {},
  timezone = true,
}: {
  value: OrgProfile;
  onChange: (next: OrgProfile) => void;
  errors?: Partial<Record<keyof OrgProfile, string>>;
  /** Setup collects the zone silently from the browser; General lets it be changed. */
  timezone?: boolean;
}) {
  const set = (k: keyof OrgProfile, v: string) => onChange({ ...value, [k]: v });
  const unitedStates = isUnitedStates(value.country);
  // Picking the United States turns a typed region into a state, and leaving it
  // drops a state that would not belong to the new country.
  const setCountry = (c: string) => {
    const code = usStateCode(value.region);
    const region = isUnitedStates(c) ? code : unitedStates && code ? "" : (value.region ?? "");
    onChange({ ...value, country: c, region });
  };
  const tzGroups = useMemo(() => timezoneGroups(value.timezone), [value.timezone]);
  return (
    <>
      <div className="field-row">
        <Field label="Contact email" hint="Where notices about the account go." error={errors.contactEmail}>
          {(p) => <input {...p} className="input" type="email" required autoComplete="off" value={value.contactEmail ?? ""} onChange={(e) => set("contactEmail", e.target.value)} placeholder="ops@example.com" />}
        </Field>
        <Field label="Phone" error={errors.phone}>
          {(p) => (
            <Suspense fallback={<input {...p} className="input" type="tel" disabled value={value.phone ?? ""} readOnly />}>
              <PhoneInput id={p.id} aria-invalid={p["aria-invalid"]} aria-describedby={p["aria-describedby"]} required value={value.phone ?? ""} onChange={(v) => set("phone", v)} />
            </Suspense>
          )}
        </Field>
      </div>
      <Field label="Website">{(p) => <input {...p} className="input" type="url" autoComplete="off" value={value.website ?? ""} onChange={(e) => set("website", e.target.value)} placeholder="https://example.com" />}</Field>
      {/* Country comes first: it decides whether the region is a state list and what the postal code is called. */}
      <Field label="Country" error={errors.country}>
        {(p) => (
          <Suspense fallback={<input {...p} className="input" disabled value={value.country ?? ""} readOnly />}>
            <CountrySelect id={p.id} aria-invalid={p["aria-invalid"]} aria-describedby={p["aria-describedby"]} required value={value.country ?? ""} onChange={setCountry} />
          </Suspense>
        )}
      </Field>
      <Field label="Street address" error={errors.addressLine1}>{(p) => <input {...p} className="input" required autoComplete="off" value={value.addressLine1 ?? ""} onChange={(e) => set("addressLine1", e.target.value)} />}</Field>
      <Field label="Suite, floor, or unit">{(p) => <input {...p} className="input" autoComplete="off" value={value.addressLine2 ?? ""} onChange={(e) => set("addressLine2", e.target.value)} />}</Field>
      <div className="field-row">
        <Field label="City" error={errors.city}>{(p) => <input {...p} className="input" required autoComplete="off" value={value.city ?? ""} onChange={(e) => set("city", e.target.value)} />}</Field>
        <Field label={unitedStates ? "State" : "State or region"} error={errors.region}>
          {(p) =>
            unitedStates ? (
              <select {...p} className="input" required value={usStateCode(value.region)} onChange={(e) => set("region", e.target.value)}>
                <option value="">Choose a state</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <input {...p} className="input" required autoComplete="off" value={value.region ?? ""} onChange={(e) => set("region", e.target.value)} />
            )
          }
        </Field>
      </div>
      <div className="field-row">
        <Field label={unitedStates ? "ZIP code" : "Postal code"} error={errors.postalCode}>{(p) => <input {...p} className="input" required autoComplete="off" value={value.postalCode ?? ""} onChange={(e) => set("postalCode", e.target.value)} />}</Field>
      </div>
      {timezone && (
        <Field label="Time zone" hint="Used for daily summaries and report dates.">
          {(p) => (
            <select {...p} className="input" value={value.timezone || browserTimezone()} onChange={(e) => set("timezone", e.target.value)}>
              {tzGroups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label} ({formatOffset(o.offsetMinutes)})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </Field>
      )}
    </>
  );
}
